/* eslint-disable */
import React, { useState, useEffect } from 'react';
import {
  Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Scatter, ComposedChart, Line,
} from 'recharts';

const C = {
  bg: 'rgba(8,12,30,0.60)',
  kant: 'rgba(255,255,255,0.08)',
  tekst: '#e2e8f0',
  subtekst: '#7a8fa6',
  success: '#22c55e',
  info: '#3b82f6',
  warning: '#f59e0b',
  danger: '#ef4444',
};

const api = async (sti) => {
  const res = await fetch('/api' + sti, { credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

const formatDato = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' });
  } catch {
    return String(d);
  }
};

export default function AnomalidedetektionDSPanel() {
  const [data, setData] = useState(null);
  const [loader, setLoader] = useState(true);
  const [fejl, setFejl] = useState(null);

  useEffect(() => {
    api('/os/ds/anomali-detektion')
      .then(d => { setData(d); setLoader(false); })
      .catch(e => { setFejl(e.message); setLoader(false); });
  }, []);

  if (loader) return <div style={{ padding: 32, color: C.subtekst, textAlign: 'center' }}>Henter anomalidata…</div>;
  if (fejl) return <div style={{ padding: 32, color: '#ef4444' }}>Fejl: {fejl}</div>;
  if (!data) return null;

  const chartData = [...(data.data || [])].reverse().map(r => ({
    dag: formatDato(r.dag),
    antal: r.antal,
    mean: r.mean_28d,
    z_score: r.z_score,
    er_anomali: r.er_anomali,
    anomali_op: r.er_anomali && r.z_score > 0 ? r.antal : null,
    anomali_ned: r.er_anomali && r.z_score < 0 ? r.antal : null,
  }));

  const anomalier = data.anomalier_seneste_5 || [];
  const totalAnomalier = data.total_anomalier || 0;

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, color: C.tekst, fontSize: 20, fontWeight: 800 }}>Hændelses-Velocity Anomalidetektion</h2>
          <div style={{ fontSize: 12, color: C.subtekst, marginTop: 4 }}>
            60 dages hændelsesvolumen · Z-score &gt; 2 markeres som anomali
          </div>
        </div>
        <div style={{
          background: totalAnomalier > 0 ? 'rgba(239,68,68,0.12)' : 'rgba(34,197,94,0.10)',
          border: `1px solid ${totalAnomalier > 0 ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.25)'}`,
          borderRadius: 8, padding: '4px 12px', fontSize: 12, fontWeight: 700,
          color: totalAnomalier > 0 ? C.danger : C.success,
        }}>
          {totalAnomalier} anomali{totalAnomalier !== 1 ? 'er' : ''} fundet
        </div>
      </div>

      <div style={{ background: C.bg, border: `1px solid ${C.kant}`, borderRadius: 12, padding: '16px 8px', marginBottom: 16 }}>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="dag" tick={{ fill: C.subtekst, fontSize: 10 }} axisLine={{ stroke: C.kant }} tickLine={false} interval={Math.floor(chartData.length / 8)} />
            <YAxis tick={{ fill: C.subtekst, fontSize: 11 }} axisLine={false} tickLine={false} width={35} />
            <Tooltip
              contentStyle={{ background: '#0a0f23', border: `1px solid ${C.kant}`, borderRadius: 8, fontSize: 12 }}
              labelStyle={{ color: C.subtekst }}
              formatter={(value, name) => {
                const labels = { antal: 'Hændelser', mean: '28d gennemsnit', anomali_op: 'Anomali (høj)', anomali_ned: 'Anomali (lav)' };
                return [value, labels[name] || name];
              }}
            />
            <Area type="monotone" dataKey="antal" fill="rgba(59,130,246,0.12)" stroke={C.info} strokeWidth={1.5} name="antal" dot={false} />
            <Line type="monotone" dataKey="mean" stroke="rgba(255,255,255,0.4)" strokeWidth={1} strokeDasharray="5 3" dot={false} name="mean" />
            <Scatter dataKey="anomali_op" fill={C.danger} name="anomali_op" shape={(props) => {
              const { cx, cy } = props;
              if (cx == null || cy == null) return null;
              return <circle cx={cx} cy={cy} r={5} fill={C.danger} stroke="#fff" strokeWidth={1} />;
            }} />
            <Scatter dataKey="anomali_ned" fill={C.success} name="anomali_ned" shape={(props) => {
              const { cx, cy } = props;
              if (cx == null || cy == null) return null;
              return <circle cx={cx} cy={cy} r={5} fill={C.success} stroke="#fff" strokeWidth={1} />;
            }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {anomalier.length > 0 && (
        <div style={{ background: C.bg, border: `1px solid ${C.kant}`, borderRadius: 12, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.tekst, marginBottom: 10 }}>5 seneste anomalier</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  {['Dato', 'Hændelser', '28d snit', 'Z-score', 'Retning'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '6px 10px', color: C.subtekst, fontWeight: 700, borderBottom: `1px solid ${C.kant}`, whiteSpace: 'nowrap', fontSize: 11 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {anomalier.map((a, i) => (
                  <tr key={i}>
                    <td style={{ padding: '6px 10px', color: C.tekst }}>{formatDato(a.dag)}</td>
                    <td style={{ padding: '6px 10px', color: C.tekst, fontWeight: 600 }}>{a.antal}</td>
                    <td style={{ padding: '6px 10px', color: C.subtekst }}>{a.mean_28d?.toFixed(1)}</td>
                    <td style={{ padding: '6px 10px', fontWeight: 700, color: a.z_score > 0 ? C.danger : C.success }}>
                      {a.z_score > 0 ? '+' : ''}{a.z_score?.toFixed(2)}
                    </td>
                    <td style={{ padding: '6px 10px', color: a.z_score > 0 ? C.danger : C.success }}>
                      {a.z_score > 0 ? 'Høj aktivitet' : 'Lav aktivitet'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {anomalier.length === 0 && (
        <div style={{
          background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.20)',
          borderRadius: 10, padding: '14px 20px', fontSize: 13, color: C.success,
        }}>
          Ingen anomalier fundet i de seneste 60 dage — hændelsesvolumen er stabil.
        </div>
      )}
    </div>
  );
}
